import { useState } from "react";
import { DetailDialog } from "../../ui/detail-dialog";
import type { PaymentMethodResponse } from "../../../hooks/requests/paymentMethod";
import type { GymPaymentMethod } from "../../../utils/zod/admin";
import { usePaymentMethodEdit } from "../../../hooks/admin/gym/usePaymentMethodEdit";
import { GATEWAY_PAYMENT_METHODS } from "../../../constants/gatewayPaymentMethods";
import { GATEWAY_PROVIDERS } from "../../../constants/gatewayProviders";

type AnyPaymentMethod = PaymentMethodResponse | GymPaymentMethod;

interface PaymentMethodDetailModalProps {
  paymentMethod: AnyPaymentMethod;
  isOpen: boolean;
  onClose: () => void;
  gymPublicId?: string;
  onUpdated?: (pm: GymPaymentMethod) => void;
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return (words[0]?.[0] ?? "").toUpperCase();
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
}

function isGatewayMethod(pm: AnyPaymentMethod): boolean {
  if ("type" in pm && pm.type === "gateway") return true;
  return false;
}

function getGatewayProviderCommission(key: string): string | undefined {
  const providerKey = key.split("_")[0];
  return GATEWAY_PROVIDERS.find((gp) => gp.key === providerKey)?.commission;
}

const CheckIcon = () => (
  <svg
    className="w-4 h-4 text-global-success"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const DashIcon = () => (
  <svg
    className="w-4 h-4 text-global-muted"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
  </svg>
);

interface SectionHeaderProps {
  label: string;
}
const SectionHeader = ({ label }: SectionHeaderProps) => (
  <div className="bg-global-overlay-secondary px-4 py-2 border-b border-global-secondary">
    <span className="text-size-2xs font-semibold uppercase tracking-wider text-global-secondary">
      {label}
    </span>
  </div>
);

interface DetailRowProps {
  label: string;
  value: string;
}
const DetailRow = ({ label, value }: DetailRowProps) => (
  <div className="flex justify-between items-center py-2 border-b border-global-secondary last:border-0">
    <span className="text-size-xs text-global-secondary uppercase tracking-wide">
      {label}
    </span>
    <span className="text-size-xs font-medium text-global-primary">
      {value}
    </span>
  </div>
);

interface EditInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "number";
  min?: string;
  step?: string;
}
const EditInput = ({
  label,
  value,
  onChange,
  type = "text",
  min,
  step,
}: EditInputProps) => (
  <div className="flex justify-between items-center py-1.5 border-b border-global-secondary last:border-0 gap-4">
    <span className="text-size-xs text-global-secondary uppercase tracking-wide shrink-0">
      {label}
    </span>
    <input
      type={type}
      value={value}
      min={min}
      step={step}
      onChange={(e) => onChange(e.target.value)}
      className="w-32 px-2 py-1 rounded-md border border-global-secondary bg-global-overlay-secondary text-global-primary text-size-xs focus:outline-none focus:border-global-accent transition-colors text-right"
    />
  </div>
);

function GatewayBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-size-2xs font-semibold bg-global-overlay-secondary text-global-accent border border-global-accent/30">
      <svg
        className="w-3 h-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
      Pasarela
    </span>
  );
}

function ViewContent({
  paymentMethod,
  showKey,
}: {
  paymentMethod: AnyPaymentMethod;
  showKey: boolean;
}) {
  const ticketConfig = paymentMethod.ticketConfig;
  const additionalSettings =
    "additionalSettings" in paymentMethod
      ? paymentMethod.additionalSettings
      : undefined;
  const commission = additionalSettings?.commission;
  const isGateway = isGatewayMethod(paymentMethod);
  const gatewayCommissionText = isGateway
    ? getGatewayProviderCommission(paymentMethod.key)
    : undefined;

  return (
    <>
      <div className="rounded-xl border border-global-secondary overflow-hidden">
        <SectionHeader label="Información general" />
        <div className="px-4 py-1">
          <DetailRow
            label="Estado"
            value={paymentMethod.active ? "Activo" : "Inactivo"}
          />
          {isGateway && <DetailRow label="Tipo" value="Pasarela de pago" />}
          {showKey && <DetailRow label="Clave" value={paymentMethod.key} />}
        </div>
      </div>

      {ticketConfig && (
        <div className="rounded-xl border border-global-secondary overflow-hidden">
          <SectionHeader label="Requiere número de ticket" />
          <div className="px-4 py-2 space-y-2">
            {(
              [
                { key: "plan", label: "Plan" },
                { key: "food", label: "Alimentación" },
                { key: "merchandise", label: "Producto" },
              ] as const
            ).map(({ key, label }) => (
              <div key={key} className="flex justify-between items-center">
                <span className="text-size-xs text-global-secondary uppercase tracking-wide">
                  {label}
                </span>
                <div className="flex items-center gap-1">
                  {ticketConfig[key].requires ? <CheckIcon /> : <DashIcon />}
                  <span className="text-size-xs text-global-primary">
                    {ticketConfig[key].requires ? "Si" : "No"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-global-secondary overflow-hidden">
        <SectionHeader label="Comisiones" />
        {gatewayCommissionText ? (
          <div className="px-4 py-3">
            <p className="text-size-xs text-global-primary leading-relaxed">
              {gatewayCommissionText}
            </p>
          </div>
        ) : commission ? (
          <div className="px-4 py-1">
            <DetailRow label="Porcentaje" value={`${commission.percentage}%`} />
            <DetailRow label="Fees fijos" value={`$${commission.fixedFee}`} />
            <DetailRow
              label="Base de cálculo"
              value={commission.calculationBase}
            />
            <DetailRow
              label="Tipo de impuesto"
              value={
                commission.tax.value !== undefined
                  ? `${commission.tax.type} (${commission.tax.value}%)`
                  : commission.tax.type
              }
            />
          </div>
        ) : (
          <div className="px-4 py-3">
            <p className="text-size-xs text-global-muted text-center">
              No configurado
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function EditContent({
  paymentMethod,
  gymPublicId,
  onUpdated,
  onCancel,
}: {
  paymentMethod: AnyPaymentMethod;
  gymPublicId: string;
  onUpdated: (pm: GymPaymentMethod) => void;
  onCancel: () => void;
}) {
  const { form, updateField, save, isSaving } = usePaymentMethodEdit(
    paymentMethod,
    gymPublicId,
    (pm) => {
      onUpdated(pm);
    },
  );

  const ticketTypes = [
    { key: "ticketPlan" as const, label: "Plan" },
    { key: "ticketFood" as const, label: "Alimentación" },
    { key: "ticketMerchandise" as const, label: "Producto" },
  ];

  return (
    <>
      <div className="rounded-xl border border-global-secondary overflow-hidden">
        <SectionHeader label="Información general" />
        <div className="px-4 py-1">
          <EditInput
            label="Nombre"
            value={form.name}
            onChange={(v) => updateField("name", v)}
          />
          <DetailRow label="Clave" value={paymentMethod.key} />
        </div>
      </div>

      <div className="rounded-xl border border-global-secondary overflow-hidden">
        <SectionHeader label="Requiere número de ticket" />
        <div className="px-4 py-2 space-y-2">
          {ticketTypes.map(({ key, label }) => (
            <label
              key={key}
              className="flex justify-between items-center cursor-pointer"
            >
              <span className="text-size-xs text-global-secondary uppercase tracking-wide">
                {label}
              </span>
              <input
                type="checkbox"
                checked={form[key]}
                onChange={(e) => updateField(key, e.target.checked)}
                className="w-4 h-4 rounded accent-global-accent"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-global-secondary overflow-hidden">
        <SectionHeader label="Comisiones" />
        <div className="px-4 py-2">
          <label className="flex justify-between items-center cursor-pointer mb-2">
            <span className="text-size-xs text-global-secondary uppercase tracking-wide">
              Agregar comisión
            </span>
            <input
              type="checkbox"
              checked={form.hasCommission}
              onChange={(e) => updateField("hasCommission", e.target.checked)}
              className="w-4 h-4 rounded accent-global-accent"
            />
          </label>

          {form.hasCommission && (
            <div className="border-t border-global-secondary pt-2 space-y-0">
              <EditInput
                label="Porcentaje"
                value={form.commissionPercentage}
                onChange={(v) => updateField("commissionPercentage", v)}
                type="number"
                min="0"
                step="0.01"
              />
              <EditInput
                label="Fees fijos"
                value={form.commissionFixedFee}
                onChange={(v) => updateField("commissionFixedFee", v)}
                type="number"
                min="0"
                step="0.01"
              />
              <div className="flex justify-between items-center py-1.5 border-b border-global-secondary last:border-0 gap-4">
                <span className="text-size-xs text-global-secondary uppercase tracking-wide shrink-0">
                  Base de cálculo
                </span>
                <select
                  value={form.commissionBase}
                  onChange={(e) =>
                    updateField(
                      "commissionBase",
                      e.target.value as "TOTAL_GROSS" | "TOTAL_NET",
                    )
                  }
                  className="px-2 py-1 rounded-md border border-global-secondary bg-global-overlay-secondary text-global-primary text-size-xs focus:outline-none focus:border-global-accent"
                >
                  <option value="TOTAL_GROSS">TOTAL_GROSS</option>
                  <option value="TOTAL_NET">TOTAL_NET</option>
                </select>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-global-secondary last:border-0 gap-4">
                <span className="text-size-xs text-global-secondary uppercase tracking-wide shrink-0">
                  Tipo de impuesto
                </span>
                <select
                  value={form.commissionTaxType}
                  onChange={(e) =>
                    updateField(
                      "commissionTaxType",
                      e.target.value as "vat" | "none" | "custom",
                    )
                  }
                  className="px-2 py-1 rounded-md border border-global-secondary bg-global-overlay-secondary text-global-primary text-size-xs focus:outline-none focus:border-global-accent"
                >
                  <option value="none">none</option>
                  <option value="vat">vat</option>
                  <option value="custom">custom</option>
                </select>
              </div>
              {form.commissionTaxType !== "none" && (
                <EditInput
                  label="Valor del impuesto"
                  value={form.commissionTaxValue}
                  onChange={(v) => updateField("commissionTaxValue", v)}
                  type="number"
                  min="0"
                  step="0.01"
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="flex-1 py-2 text-size-xs font-semibold rounded-lg bg-global-overlay-secondary text-global-secondary hover:text-global-primary transition-colors cursor-pointer disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={isSaving || !form.name.trim()}
          className="flex-1 py-2 text-size-xs font-semibold rounded-lg btn-global-primary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer"
        >
          {isSaving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </>
  );
}

export const PaymentMethodDetailModal = ({
  paymentMethod,
  isOpen,
  onClose,
  gymPublicId,
  onUpdated,
}: PaymentMethodDetailModalProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const isGateway = isGatewayMethod(paymentMethod);
  const canEdit = !!gymPublicId && !!onUpdated && !isGateway;
  const additionalSettings =
    "additionalSettings" in paymentMethod
      ? paymentMethod.additionalSettings
      : undefined;

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const handleUpdated = (pm: GymPaymentMethod) => {
    setIsEditing(false);
    onUpdated?.(pm);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const buttons = isEditing
    ? undefined
    : [
        ...(canEdit
          ? [
              {
                label: "Editar",
                onClick: () => setIsEditing(true),
                variant: "default" as const,
                type: "button" as const,
              },
            ]
          : []),
        {
          label: "Cerrar",
          onClick: handleClose,
          variant: "secondary" as const,
          type: "button" as const,
        },
      ];

  const gatewayLogo = GATEWAY_PAYMENT_METHODS.find(
    (gw) => gw.key === paymentMethod.key,
  )?.logo;

  return (
    <DetailDialog
      open={isOpen}
      onOpenChange={(o) => !o && handleClose()}
      title={isEditing ? "Editar método de pago" : "Detalle del método de pago"}
      maxWidth="lg"
      buttons={buttons}
      bodyClassName="p-5 space-y-4"
    >
      <div className="flex items-center gap-4">
        {gatewayLogo ? (
          <div className="w-14 h-14 shrink-0 rounded-full bg-white border border-global-secondary flex items-center justify-center p-2">
            <img
              src={gatewayLogo}
              alt={paymentMethod.name}
              className="h-full w-full object-contain"
            />
          </div>
        ) : additionalSettings?.logo_url ? (
          <img
            src={additionalSettings.logo_url}
            alt={paymentMethod.name}
            className="w-14 h-14 rounded-full object-cover border border-global-secondary"
          />
        ) : (
          <div className="w-14 h-14 rounded-full btn-global-primary flex items-center justify-center shrink-0 shadow-global-button ring-1 ring-global-accent">
            <span className="font-bold text-lg leading-none">
              {getInitials(paymentMethod.name)}
            </span>
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-size-base font-bold text-global-title truncate">
              {paymentMethod.name}
            </p>
            {isGateway && <GatewayBadge />}
          </div>
          {canEdit && (
            <p className="text-size-xs text-global-secondary font-mono">
              {paymentMethod.key}
            </p>
          )}
        </div>
      </div>

      {isEditing && canEdit ? (
        <EditContent
          paymentMethod={paymentMethod}
          gymPublicId={gymPublicId}
          onUpdated={handleUpdated}
          onCancel={handleCancelEdit}
        />
      ) : (
        <ViewContent paymentMethod={paymentMethod} showKey={canEdit} />
      )}
    </DetailDialog>
  );
};
